import { useState, useMemo } from "react";
import { useLanguage } from "@/hooks/use-language";
import { useEvents } from "@/hooks/use-events";
import { useAuth } from "@/hooks/use-auth";
import { useIsMobile } from "@/hooks/use-mobile";
import { format, startOfWeek, addDays, addWeeks, isSameDay, getWeek } from "date-fns";
import { nb, enUS } from "date-fns/locale";
import { ChevronLeft, ChevronRight, MapPin, Users, Pencil, Repeat, CalendarPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getGroupColorClasses, getLevelGroupName } from "@/utils/groupColors";
import { EventTooltip } from "@/components/calendar/EventTooltip";
import { EditEventDialog } from "@/components/admin/EditEventDialog";
import { CreateEventDialog } from "@/components/admin/CreateEventDialog";
import { cn } from "@/lib/utils";
import { formatEventTime } from "@/utils/dateUtils";
import type { EventData } from "@/contexts/event/EventContext";

export default function Calendar() {
  const { t, language } = useLanguage();
  const { events } = useEvents();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [currentMobileDate, setCurrentMobileDate] = useState(new Date());
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [editEventDialogOpen, setEditEventDialogOpen] = useState(false);
  const [createEventDialogOpen, setCreateEventDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventData | null>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const isAdmin = user?.isAdmin || false;
  const minSwipeDistance = 50;

  const locale = language === 'no' ? nb : enUS;

  // Mobile two-day range
  const mobileDays = useMemo(() => {
    return [currentMobileDate, addDays(currentMobileDate, 1)];
  }, [currentMobileDate]);

  // Generate week days
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  }, [currentWeekStart]);

  // Time slots from 08:00 to 22:00
  const timeSlots = useMemo(() => {
    return Array.from({ length: 14 }, (_, i) => `${(8 + i).toString().padStart(2, '0')}:00`);
  }, []);

  // Filter events for current week (desktop)
  const weekEvents = useMemo(() => {
    return events.filter(event => {
      const eventDate = new Date(event.date);
      const isInWeek = weekDays.some(day => isSameDay(day, eventDate));
      
      if (!isInWeek) return false;
      
      // Filter by selected levels if any
      if (selectedLevels.length > 0) {
        const groupName = event.groupKey || event.group;
        return selectedLevels.includes(groupName);
      }
      
      return true;
    });
  }, [events, weekDays, selectedLevels]);

  // Filter events for current mobile view (2-day range)
  const mobileDayEvents = useMemo(() => {
    return events.filter(event => {
      const eventDate = new Date(event.date);
      const isInMobileDays = mobileDays.some(day => isSameDay(day, eventDate));
      
      if (!isInMobileDays) return false;
      
      // Filter by selected levels if any
      if (selectedLevels.length > 0) {
        const groupName = event.groupKey || event.group;
        return selectedLevels.includes(groupName);
      }
      
      return true;
    });
  }, [events, mobileDays, selectedLevels]);

  const levels = [
    { key: "event.group.beginners", label: "Nybegynner / Beginner", variant: "beginners" },
    { key: "groups.subgroup.intermediate", label: "Middels / Intermediate", variant: "intermediate" },
    { key: "groups.subgroup.intermediate_plus", label: "Middels Pluss / Intermediate Plus", variant: "intermediate-plus" },
    { key: "groups.subgroup.advanced", label: "Avansert / Advanced", variant: "advanced" },
  ];

  const toggleLevel = (levelKey: string) => {
    setSelectedLevels(prev => 
      prev.includes(levelKey) 
        ? prev.filter(k => k !== levelKey)
        : [...prev, levelKey]
    );
  };

  const goToPreviousWeek = () => setCurrentWeekStart(prev => addWeeks(prev, -1));
  const goToNextWeek = () => setCurrentWeekStart(prev => addWeeks(prev, 1));

  // Mobile navigation
  const goToPreviousDayPair = () => setCurrentMobileDate(prev => addDays(prev, -2));
  const goToNextDayPair = () => setCurrentMobileDate(prev => addDays(prev, 2));
  const goToToday = () => setCurrentMobileDate(new Date());

  // Touch handlers for swipe
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) goToNextDayPair();
    if (isRightSwipe) goToPreviousDayPair();
  };

  const getEventForDayAndTime = (day: Date, time: string, eventsToFilter: EventData[]) => {
    return eventsToFilter.filter(event => {
      if (!isSameDay(new Date(event.date), day)) return false;
      // Match events that start within the hour slot
      const eventTime = event.time.split(':')[0]; // Get the hour part (e.g., "14" from "14:20")
      const slotHour = time.split(':')[0]; // Get the hour part (e.g., "14" from "14:00")
      return eventTime === slotHour;
    });
  };

  const getCourtsCount = (maxParticipants: number) => {
    return Math.ceil(maxParticipants / 4);
  };

  const getAvailableSpots = (event: typeof events[0]) => {
    return event.maxParticipants - event.participants.length;
  };

  return (
    <>
      {/* Mobile View - Two Day Calendar */}
      <div className="lg:hidden flex flex-col min-h-screen bg-background">
        {/* Mobile Header */}
        <div className="border-b bg-card p-3 space-y-2">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold">{t("nav.calendar")}</h1>
            <Button variant="outline" size="sm" onClick={goToToday}>
              {t("calendar.today") || "Today"}
            </Button>
          </div>
          
          {/* Date Navigation */}
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={goToPreviousDayPair}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-sm font-medium">
              {format(mobileDays[0], 'EEE d', { locale })} - {format(mobileDays[1], 'EEE d MMM', { locale })}
            </div>
            <Button variant="ghost" size="icon" onClick={goToNextDayPair}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Level Filters - Horizontal Scroll */}
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-3 px-3">
            {levels.map((level) => {
              const colorClasses = getGroupColorClasses(level.label);
              const isSelected = selectedLevels.includes(level.key);
              
              return (
                <Badge
                  key={level.key}
                  variant="outline"
                  className={cn(
                    "cursor-pointer transition-opacity text-xs border-transparent whitespace-nowrap",
                    colorClasses.bg,
                    colorClasses.text,
                    !isSelected && selectedLevels.length > 0 && "opacity-40"
                  )}
                  onClick={() => toggleLevel(level.key)}
                >
                  {language === 'no' ? level.label.split(' / ')[0] : level.label.split(' / ')[1]}
                </Badge>
              );
            })}
            {selectedLevels.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setSelectedLevels([])}>
                {t("common.clear")}
              </Button>
            )}
          </div>
        </div>
        
        {/* Scrollable Grid with Swipe Support */}
        <div 
          className="pb-20"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="min-w-full">
            {/* Day Headers */}
            <div className="grid grid-cols-[60px_1fr_1fr] border-b sticky top-0 bg-card z-10">
              <div className="p-2"></div>
              {mobileDays.map((day, i) => (
                <div key={i} className="p-2 text-center border-l">
                  <div className="font-semibold text-sm">
                    {format(day, 'EEE', { locale })}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {format(day, 'd MMM', { locale })}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Time Slots */}
            {timeSlots.map((time) => (
              <div key={time} className="grid grid-cols-[60px_1fr_1fr] border-b min-h-[50px]">
                <div className="p-1.5 border-r text-xs text-muted-foreground sticky left-0 bg-card">
                  {time}
                </div>
                {mobileDays.map((day, dayIndex) => {
                  const eventsAtSlot = getEventForDayAndTime(day, time, mobileDayEvents);
                  return (
                    <div key={dayIndex} className="border-l p-1.5">
                      {eventsAtSlot.map((event) => {
                        const availableSpots = getAvailableSpots(event);
                        const isFull = availableSpots === 0;
                        
                        // Always use subgroup colors from the design system
                        const colorClasses = getGroupColorClasses(event.group || '');
                        const eventClassName = `${colorClasses.bg} ${colorClasses.text}`;

                        return (
                          <EventTooltip key={event.id} event={event} isMobile={isMobile}>
                            <div
                              className={cn(
                                "rounded p-1.5 mb-1 text-xs cursor-pointer",
                                eventClassName
                              )}
                            >
                              <div className="grid grid-cols-2 gap-1 items-center">
                                <div className="font-bold text-sm">
                                  {formatEventTime(event.time)}
                                </div>
                                <div className="flex items-center justify-end gap-1">
                                  {event.parentEventId && (
                                    <Repeat className="h-3 w-3 opacity-70" />
                                  )}
                                  {isFull ? (
                                    <Badge variant="destructive" className="text-xs px-1.5 py-0.5 h-5">
                                      {t("calendar.full")}
                                    </Badge>
                                  ) : (
                                    <Badge variant="secondary" className="text-sm px-2 py-0.5 h-5 font-bold">
                                      {availableSpots}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </EventTooltip>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Mobile FAB for Admin */}
        {isAdmin && (
            <Button
              size="icon"
              className="fixed bottom-24 right-4 h-14 w-14 rounded-full shadow-xl z-20 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 hover:shadow-2xl hover:scale-105 transition-all duration-200"
              onClick={() => setCreateEventDialogOpen(true)}
            >
              <CalendarPlus className="h-7 w-7 text-white" strokeWidth={2} />
            </Button>
        )}
      </div>

      {/* Desktop View - Weekly Calendar */}
      <div className="hidden lg:block min-h-screen bg-background">
        {/* Header */}
        <div className="border-b bg-card sticky top-0 z-20">
          <div className="p-3">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-2xl font-bold">{t("nav.calendar")}</h1>
              
              {/* Week Navigation */}
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={goToPreviousWeek}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="text-base font-medium min-w-[100px] text-center">
                  {t("calendar.week")} {getWeek(currentWeekStart, { weekStartsOn: 1, locale })}
                </div>
                <Button variant="outline" size="icon" onClick={goToNextWeek}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Level Filters */}
            <div className="flex gap-2 flex-wrap">
              {levels.map((level) => {
                const colorClasses = getGroupColorClasses(level.label);
                const isSelected = selectedLevels.includes(level.key);
                
                return (
                <Badge
                  key={level.key}
                  variant="outline"
                  className={cn(
                    "cursor-pointer transition-opacity text-xs border-transparent",
                    colorClasses.bg,
                    colorClasses.text,
                    !isSelected && selectedLevels.length > 0 && "opacity-40"
                  )}
                  onClick={() => toggleLevel(level.key)}
                >
                  {language === 'no' ? level.label.split(' / ')[0] : level.label.split(' / ')[1]}
                </Badge>
                );
              })}
              {selectedLevels.length > 0 && (
                <Button variant="ghost" size="sm" onClick={() => setSelectedLevels([])}>
                  {t("common.clear")}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="pb-20">
          <div className="min-w-[1200px]">
            {/* Day Headers */}
            <div className="grid border-b bg-card" style={{ gridTemplateColumns: '50px repeat(7, minmax(0, 1fr))' }}>
              <div className="px-1 py-1 border-r"></div>
              {weekDays.map((day, i) => (
                <div key={i} className="px-1 py-1 text-center border-r min-w-0">
                  <div className="font-semibold text-sm truncate">{format(day, 'EEE', { locale })}</div>
                  <div className="text-xs text-muted-foreground truncate">{format(day, 'd MMM', { locale })}</div>
                </div>
              ))}
            </div>

            {/* Time Grid */}
            {timeSlots.map((time) => (
              <div key={time} className="grid border-b min-h-[32px]" style={{ gridTemplateColumns: '50px repeat(7, minmax(0, 1fr))' }}>
                <div className="px-1 py-1 border-r text-xs text-muted-foreground font-medium">
                  {time}
                </div>
                {weekDays.map((day, dayIndex) => {
                  const eventsAtSlot = getEventForDayAndTime(day, time, weekEvents);
                  
                  return (
                    <div key={dayIndex} className="border-r p-1 min-w-0 overflow-hidden">
                      {eventsAtSlot.length > 0 && (
                        <div className="flex gap-1 min-w-0">
                          {eventsAtSlot.map((event) => {
                            const availableSpots = getAvailableSpots(event);
                            const isFull = availableSpots === 0;
                            
                            // Always use subgroup colors from the design system
                            const colorClasses = getGroupColorClasses(event.group || '');
                            const eventClassName = `${colorClasses.bg} ${colorClasses.text}`;

                            return (
                              <EventTooltip key={event.id} event={event} isMobile={false}>
                                <div
                                  className={cn(
                                    "rounded px-1 py-0.5 cursor-pointer transition-all hover:scale-105 hover:shadow-lg flex-1 group relative",
                                    eventClassName
                                  )}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1">
                                      {event.parentEventId && (
                                        <Repeat className="h-3 w-3 opacity-70" />
                                      )}
                                      {isFull ? (
                                        <Badge variant="destructive" className="text-xs px-1.5 py-0 h-5">
                                          {t("calendar.full")}
                                        </Badge>
                                      ) : (
                                        <Badge variant="secondary" className="text-xs px-1.5 py-0 h-5 font-bold">
                                          {availableSpots}
                                        </Badge>
                                      )}
                                    </div>
                                    {isAdmin && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedEvent(event);
                                          setEditEventDialogOpen(true);
                                        }}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity ml-1"
                                      >
                                        <Pencil className="h-3 w-3" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </EventTooltip>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Desktop FAB for Admin */}
        {isAdmin && (
          <Button
            size="lg"
            variant="ghost"
            className="fixed bottom-24 right-6 h-14 w-14 rounded-full shadow-xl bg-blue-500 hover:bg-blue-600 hover:shadow-2xl hover:scale-105 transition-all duration-200 dark:bg-blue-600 dark:hover:bg-blue-700"
            onClick={() => setCreateEventDialogOpen(true)}
          >
            <CalendarPlus className="h-6 w-6 text-white" />
          </Button>
        )}
      </div>

      {/* Dialogs (shared between mobile and desktop) */}
      {isAdmin && (
        <>
          <EditEventDialog
            open={editEventDialogOpen}
            onOpenChange={setEditEventDialogOpen}
            event={selectedEvent}
            onEventUpdated={() => window.location.reload()}
          />
          
          <CreateEventDialog
            open={createEventDialogOpen}
            onOpenChange={setCreateEventDialogOpen}
            onEventCreated={() => window.location.reload()}
          />
        </>
      )}
    </>
  );
}
